<script setup lang="ts">
import type { StoryblokAsset } from '../../types'

interface Props {
  blok: {
    title: string
    title_word_mark_image?: StoryblokAsset
    title_word_to_mark?: string
    title_mobile?: string
    title_word_to_mark_mobile?: string
    background_image?: StoryblokAsset
    background_color?: string
    highlights: any[]
  }
}

const props = defineProps<Props>()
const imageOptimized = computed(() => props.blok.background_image ? optimizeImage(props.blok.background_image.filename) : undefined)

const highlightWords = (text: string, word: string, image: string) => {
  const regex = new RegExp(`\\b(${word})\\b`, 'gi')
  return text.replace(regex, `<span class="title-highlight">$1<img src='${image}'></span>`)
}

const mainTitle = computed(() => {
  if (props.blok.title_word_to_mark && props.blok.title_word_mark_image)
    return highlightWords(props.blok.title, props.blok.title_word_to_mark, props.blok.title_word_mark_image.filename)

  return props.blok.title
})

const titleMobile = computed(() => {
  if (props.blok.title_word_to_mark_mobile && props.blok.title_word_mark_image && props.blok.title_mobile)
    return highlightWords(props.blok.title_mobile, props.blok.title_word_to_mark_mobile, props.blok.title_word_mark_image.filename)

  return props.blok.title_mobile
})
</script>

<template>
  <div
    v-editable="blok"
    :class="$style.container"
    bg-gradient-to-b
    from-gray-lighter
    to-white
    class="py-[100px] lg:py-[200px] px-[20px]"
    :style="{
      backgroundImage: imageOptimized ? `url(${imageOptimized})` : 'none',
    }"
  >
    <div class="max-w-[1280px] mx-auto flex flex-col md:flex-row justify-start md:justify-center lg:justify-between gap-10 md:items-center">
      <div
        class="title hidden lg:block"
        v-html="mainTitle"
      />
      <div class="flex flex-col lg:hidden">
        <div class="title" v-html="titleMobile" />
      </div>
      <div class="flex flex-col lg:flex-row gap-[40px] lg:gap-[60px]">
        <StoryblokComponent v-for="highlight in blok.highlights" :key="highlight._uid" :blok="highlight" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.title {
  @apply font-circular-bold font-bold text-[45.23px] leading-[49.753px] tracking-[-1.809px] max-w-[497px];
}
.title-highlight {
  position: relative;
  display: inline-block;
}

.title-highlight img {
  position: absolute;
  bottom: -8px;
  left: 0;
}
</style>

<style module>
.container {
  background-color: var(--background-color);
  background-image: var(--background-image);
  background-size: cover;
}
</style>
